import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  AttachFile,
  DateRange,
  AccountBalance,
  Search,
  RestartAlt,
  FileDownload,
  FolderOpen,
  Business,
  Phone,
  Email,
  LocationOn,
  Download,
  Visibility,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  TableChart,
  MoreVert,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { transactionsApi, attachmentsApi } from '../services/api';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import AttachmentPreviewDialog, { getPreviewType } from './AttachmentPreviewDialog';

const TYPE_LABELS = {
  PurchaseOrder: 'Purchase Order',
  SalesOrder: 'Sales Order',
  Bill: 'Bill',
  Invoice: 'Invoice',
  VendorCredit: 'Vendor Credit',
  BillPaymentCheck: 'Bill Payment (Check)',
  BillPaymentCreditCard: 'Bill Payment (CC)',
  ReceivePayment: 'Receive Payment',
  ItemReceipt: 'Item Receipt',
};

const TYPE_SHORT = {
  PurchaseOrder: 'PO',
  SalesOrder: 'SO',
  Bill: 'Bill',
  Invoice: 'Inv',
  VendorCredit: 'VC',
  BillPaymentCheck: 'BPChk',
  BillPaymentCreditCard: 'BPCC',
  ReceivePayment: 'RcvPmt',
  ItemReceipt: 'IR',
};

const TYPE_COLORS = {
  PurchaseOrder: '#32578b',
  SalesOrder: '#2e7d32',
  Bill: '#ed6c02',
  Invoice: '#9c27b0',
  VendorCredit: '#d32f2f',
  BillPaymentCheck: '#0277bd',
  BillPaymentCreditCard: '#00838f',
  ReceivePayment: '#558b2f',
  ItemReceipt: '#6a1b9a',
};

function getFileIcon(fileType) {
  if (!fileType) return <InsertDriveFile />;
  if (fileType.includes('pdf')) return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
  if (fileType.includes('image')) return <Image sx={{ color: '#388e3c' }} />;
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv'))
    return <TableChart sx={{ color: '#1b5e20' }} />;
  return <InsertDriveFile sx={{ color: '#757575' }} />;
}

function formatFileType(att) {
  const type = (att.fileType || '').toLowerCase();
  const ext = (att.fileName || '').split('.').pop().toLowerCase();

  if (type.includes('pdf') || ext === 'pdf') return 'PDF';
  if (type === 'image/jpeg' || ext === 'jpg' || ext === 'jpeg') return 'JPEG Image';
  if (type === 'image/png' || ext === 'png') return 'PNG Image';
  if (type.startsWith('image/')) return 'Image';
  if (type.includes('wordprocessingml') || ext === 'docx') return 'Word Document';
  if (type === 'application/msword' || ext === 'doc') return 'Word Document';
  if (type.includes('spreadsheetml') || ext === 'xlsx') return 'Excel Spreadsheet';
  if (type.includes('ms-excel') || ext === 'xls') return 'Excel Spreadsheet';
  if (type === 'text/csv' || ext === 'csv') return 'CSV';
  if (type.includes('ms-outlook') || ext === 'msg') return 'Outlook Email';
  if (type === 'message/rfc822' || ext === 'eml') return 'Email';
  if (type === 'text/plain' || ext === 'txt') return 'Text File';
  if (type.includes('text/html') || ext === 'htm' || ext === 'html') return 'HTML';
  return ext.toUpperCase();
}

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Mobile: single MoreVert button opens menu; Desktop: individual Preview + Download buttons
function EntityDocActions({ att, onPreview, onDownload, canPreview }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isMobile) {
    return (
      <>
        {canPreview ? (
          <Tooltip title="Preview">
            <IconButton size="small" onClick={() => onPreview(att)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="This file type cannot be previewed — download to view">
            <span>
              <IconButton size="small" disabled>
                <Visibility fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => onDownload(att)}>
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {canPreview && (
          <MenuItem onClick={() => { setAnchorEl(null); onPreview(att); }}>
            <Visibility fontSize="small" sx={{ mr: 1 }} /> Preview
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchorEl(null); onDownload(att); }}>
          <Download fontSize="small" sx={{ mr: 1 }} /> Download
        </MenuItem>
      </Menu>
    </>
  );
}

function EntityDetail() {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const { pushCrumb } = useBreadcrumbs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('txnDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Search/filter state
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hideWithoutAttachments, setHideWithoutAttachments] = useState(false);
  const [searchLineItems, setSearchLineItems] = useState(false);

  // Attachment preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // Push breadcrumb only once per entity navigation
  const crumbPushedRef = useRef(null);

  const loadEntity = useCallback(async () => {
    try {
      setLoading(true);
      const result = await transactionsApi.getEntityDetail(entityId, {
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        search: search || undefined,
        transactionType: filterType || undefined,
        status: filterStatus || undefined,
        amountMin: amountMin || undefined,
        amountMax: amountMax || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        hasAttachments: hideWithoutAttachments ? 'true' : undefined,
        searchLineItems: (search && searchLineItems) ? 'true' : undefined,
      });
      setData(result);
      if (crumbPushedRef.current !== entityId && result.entity) {
        pushCrumb(`/entity/${entityId}`, result.entity.entityName);
        crumbPushedRef.current = entityId;
      }
    } catch (err) {
      console.error('Error loading entity:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, page, rowsPerPage, sortBy, sortOrder, pushCrumb, search, filterType, filterStatus, amountMin, amountMax, dateFrom, dateTo, hideWithoutAttachments, searchLineItems]);

  useEffect(() => {
    loadEntity();
  }, [loadEntity]);

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterStatus('');
    setAmountMin('');
    setAmountMax('');
    setDateFrom('');
    setDateTo('');
    setHideWithoutAttachments(false);
    setSearchLineItems(false);
    setPage(0);
  };

  const hasFilters = search || filterType || filterStatus || amountMin || amountMax || dateFrom || dateTo || hideWithoutAttachments;

  const handleExportCsv = async () => {
    if (!data?.entity) return;
    const params = {
      entity: data.entity.entityName,
      search: search || undefined,
      transactionType: filterType || undefined,
      status: filterStatus || undefined,
      amountMin: amountMin || undefined,
      amountMax: amountMax || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      sortBy,
      sortOrder,
    };
    try {
      await transactionsApi.exportCsv(params);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handlePreview = (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const handleDownload = (attachment) => {
    const url = attachmentsApi.getDownloadUrl(attachment.id);
    window.open(url, '_blank');
  };

  const formatAddress = (addr) => {
    if (!addr || typeof addr !== 'object') return null;
    const parts = [addr.addr1, addr.addr2, addr.addr3, addr.addr4, addr.addr5].filter(Boolean);
    const cityStateZip = [addr.city, addr.state].filter(Boolean).join(', ');
    if (addr.postalcode) {
      parts.push(`${cityStateZip} ${addr.postalcode}`.trim());
    } else if (cityStateZip) {
      parts.push(cityStateZip);
    }
    return parts.length > 0 ? parts.join('\n') : null;
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <div className="page-container">
        <Paper sx={{ p: 4, maxWidth: 800, width: '100%', textAlign: 'center' }}>
          <Typography color="error" gutterBottom>
            {error || 'Entity not found'}
          </Typography>
          <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        </Paper>
      </div>
    );
  }

  const { entity, stats, masterData, entityAttachments } = data;

  return (
    <div className="page-container">
      {/* Header */}
      <Paper sx={{ p: 3, width: '100%', maxWidth: 1200 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Chip
            label={entity.entityType || 'Entity'}
            size="small"
            sx={{
              backgroundColor: entity.entityType === 'Customer' ? '#2e7d32' : '#32578b',
              color: '#fff',
              fontWeight: 'bold',
            }}
          />
          <Typography variant="h5" fontWeight="bold" color="primary">
            {entity.entityName}
          </Typography>
        </Box>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '&:last-child': { pb: 1.5 } }}>
                <Description sx={{ color: '#32578b', fontSize: 28, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                  {stats.totalTransactions.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">Transactions</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '&:last-child': { pb: 1.5 } }}>
                <AccountBalance sx={{ color: '#32578b', fontSize: 28, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                  {stats.totalAmount
                    ? isMobile
                      ? `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(parseFloat(stats.totalAmount))}`
                      : `$${parseFloat(stats.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                    : '$0'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '&:last-child': { pb: 1.5 } }}>
                <AttachFile sx={{ color: '#32578b', fontSize: 28, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                  {stats.totalAttachments.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">Txn Attachments</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '&:last-child': { pb: 1.5 } }}>
                <DateRange sx={{ color: '#32578b', fontSize: 28, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, lineHeight: 1.3 }}>
                  {isMobile ? (
                    <>
                      {stats.dateRange.min ? format(new Date(stats.dateRange.min), 'MM/yy') : '—'}
                      {'–'}
                      {stats.dateRange.max ? format(new Date(stats.dateRange.max), 'MM/yy') : '—'}
                    </>
                  ) : (
                    <>
                      {stats.dateRange.min ? format(new Date(stats.dateRange.min), 'MM/yyyy') : '—'}
                      {' — '}
                      {stats.dateRange.max ? format(new Date(stats.dateRange.max), 'MM/yyyy') : '—'}
                    </>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">Date Range</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Type breakdown */}
        {stats.typeCounts && Object.keys(stats.typeCounts).length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(stats.typeCounts).map(([type, count]) => (
              <Chip
                key={type}
                label={`${count} ${TYPE_LABELS[type] || type}${count !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  backgroundColor: TYPE_COLORS[type] || '#757575',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Master Data Card */}
      {masterData && (
        <Paper sx={{ p: 3, width: '100%', maxWidth: 1200 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: '#32578b' }} />
            {entity.entityType === 'Customer' ? 'Customer' : 'Vendor'} Information
          </Typography>
          <Grid container spacing={3}>
            {/* Contact info column */}
            <Grid item xs={12} sm={4}>
              {masterData.companyName && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Company:</strong> {masterData.companyName}
                </Typography>
              )}
              {masterData.contact && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Contact:</strong> {masterData.contact}
                </Typography>
              )}
              {masterData.phone && (
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {masterData.phone}
                </Typography>
              )}
              {masterData.altPhone && (
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {masterData.altPhone} (alt)
                </Typography>
              )}
              {masterData.email && (
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {masterData.email}
                </Typography>
              )}
              {masterData.accountNumber && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Account #:</strong> {masterData.accountNumber}
                </Typography>
              )}
            </Grid>

            {/* Address column */}
            <Grid item xs={12} sm={4}>
              {(() => {
                const addr = formatAddress(masterData.vendorAddress || masterData.billAddress);
                return addr ? (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3 }} />
                      <span style={{ whiteSpace: 'pre-line' }}>{addr}</span>
                    </Typography>
                  </Box>
                ) : null;
              })()}
              {masterData.terms && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Terms:</strong> {masterData.terms}
                </Typography>
              )}
              {masterData.balance != null && parseFloat(masterData.balance) !== 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Balance:</strong> ${parseFloat(masterData.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Typography>
              )}
            </Grid>

            {/* Vendor-specific / status column */}
            <Grid item xs={12} sm={4}>
              {entity.entityType !== 'Customer' && (
                <>
                  {masterData.taxId && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Tax ID:</strong> {masterData.taxId}
                    </Typography>
                  )}
                  {masterData.is1099 && (
                    <Chip label="1099 Vendor" size="small" color="info" variant="outlined" sx={{ mb: 1 }} />
                  )}
                  {masterData.nameOnCheck && masterData.nameOnCheck !== masterData.name && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name on Check:</strong> {masterData.nameOnCheck}
                    </Typography>
                  )}
                </>
              )}
              {entity.entityType === 'Customer' && masterData.salesRep && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sales Rep:</strong> {masterData.salesRep}
                </Typography>
              )}
              <Chip
                label={masterData.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={masterData.isActive ? 'success' : 'default'}
                variant="outlined"
                sx={{ mb: 1 }}
              />
              {masterData.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.8rem' }}>
                  {masterData.notes}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Entity Documents */}
      {masterData && (
        <Paper sx={{ width: '100%', maxWidth: 1200, position: 'relative' }}>
          {/* Gradient scroll hint */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
          <Box sx={{ p: 3, pb: 0 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOpen sx={{ color: '#32578b' }} />
              {entity.entityType === 'Customer' ? 'Customer' : 'Vendor'} Documents
              {entityAttachments?.length > 0 && (
                <Chip label={entityAttachments.length} size="small" color="primary" sx={{ ml: 1 }} />
              )}
            </Typography>
          </Box>
          {!entityAttachments || entityAttachments.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No documents attached to this {entity.entityType === 'Customer' ? 'customer' : 'vendor'}.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Size</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityAttachments.map((att) => {
                    const canPreview = getPreviewType(att) !== null;
                    return (
                      <TableRow key={att.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getFileIcon(att.fileType)}
                            <Typography variant="body2" sx={{
                              maxWidth: { xs: 140, sm: 'none' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {att.fileName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileType(att)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2">{formatFileSize(att.fileSize)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <EntityDocActions
                            att={att}
                            onPreview={handlePreview}
                            onDownload={handleDownload}
                            canPreview={canPreview}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Search & Filters */}
      <Paper sx={{ p: 2, width: '100%', maxWidth: 1200 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search sx={{ color: '#32578b' }} />
          Search & Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              placeholder="Search ref #, memo..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
                <MenuItem value="Received">Received</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <TextField
              fullWidth type="number" label="Min $" value={amountMin}
              onChange={(e) => { setAmountMin(e.target.value); setPage(0); }}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <TextField
              fullWidth type="number" label="Max $" value={amountMax}
              onChange={(e) => { setAmountMax(e.target.value); setPage(0); }}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth type="date" label="From" value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                if (!e.target.value || (dateTo && dateTo < e.target.value)) setDateTo('');
                setPage(0);
              }}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth type="date" label="To" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              size="small" InputLabelProps={{ shrink: true }}
              disabled={!dateFrom}
              inputProps={{ min: dateFrom }}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {hasFilters && (
                <Button
                  variant="outlined" size="medium" startIcon={<RestartAlt />}
                  onClick={clearFilters} sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="outlined" size="medium" startIcon={<FileDownload />}
                onClick={handleExportCsv} sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
              >
                Export CSV
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideWithoutAttachments}
                onChange={(e) => { setHideWithoutAttachments(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Hide documents without attachments
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchLineItems}
                onChange={(e) => { setSearchLineItems(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={<Typography variant="body2" color="text.secondary">Include line items in search</Typography>}
          />
        </Box>
      </Paper>

      {/* Transactions Table */}
      <Box sx={{ position: 'relative', maxWidth: 1200, width: '100%' }}>
        {/* Gradient scroll hint — fades right edge to indicate horizontal scrollability */}
        <Box sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 48,
          background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))',
          pointerEvents: 'none',
          zIndex: 1,
          borderRadius: '0 4px 4px 0',
        }} />
      <TableContainer component={Paper} sx={{ maxWidth: 1200, width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#32578b' }} />
            Transactions
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>
                <TableSortLabel
                  active={sortBy === 'transactionType'}
                  direction={sortBy === 'transactionType' ? sortOrder : 'asc'}
                  onClick={() => handleSort('transactionType')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'refNumber'}
                  direction={sortBy === 'refNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSort('refNumber')}
                >
                  Ref #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'txnDate'}
                  direction={sortBy === 'txnDate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('txnDate')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'amount'}
                  direction={sortBy === 'amount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Attachments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No transactions found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((txn) => (
                <TableRow
                  key={txn.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      window.open(`/transaction/${txn.id}`, '_blank');
                    } else {
                      navigate(`/transaction/${txn.id}`);
                    }
                  }}
                  onAuxClick={(e) => {
                    if (e.button === 1) { e.preventDefault(); window.open(`/transaction/${txn.id}`, '_blank'); }
                  }}
                >
                  <TableCell>
                    <Chip
                      label={TYPE_SHORT[txn.transactionType] || txn.transactionType}
                      size="small"
                      sx={{
                        backgroundColor: TYPE_COLORS[txn.transactionType] || '#757575',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        minWidth: 36,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RouterLink
                      to={`/transaction/${txn.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {txn.refNumber}
                      </Typography>
                    </RouterLink>
                  </TableCell>
                  <TableCell>
                    {txn.txnDate ? format(new Date(txn.txnDate), 'MM/dd/yyyy') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {txn.amount ? `$${parseFloat(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {txn._count?.attachments > 0 ? (
                      <Chip
                        icon={<AttachFile />}
                        label={txn._count.attachments}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">None</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.pagination.total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } } }}
        />
      </TableContainer>
      </Box>{/* end scroll hint wrapper */}

      {/* Attachment Preview Dialog */}
      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        attachment={previewAttachment}
        onDownload={handleDownload}
      />
    </div>
  );
}

export default EntityDetail;
